import { ExternalLink, TicketCheck, LogIn, PlusCircle, Eye, MessageSquare } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const STEPS = [
  {
    icon: LogIn,
    title: 'Log in to NexaRise Support',
    description: (
      <>
        Visit{' '}
        <a
          href="https://support.nicecare.co/chat"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-gold-600 underline underline-offset-2 hover:text-gold-700"
        >
          support.nicecare.co/chat
        </a>{' '}
        and sign in using the <span className="font-medium">same email and password</span> you use to log in to CHHABIGHAR.
      </>
    ),
  },
  {
    icon: PlusCircle,
    title: 'Create a new ticket',
    description:
      'Once logged in, click "New Ticket" or "Raise Issue". Describe the problem you are facing in detail — include screenshots if possible. Select the relevant category (e.g. Billing, Inventory, POS, etc.).',
  },
  {
    icon: MessageSquare,
    title: 'Add details & submit',
    description:
      'Fill in the subject and description clearly. The more detail you provide, the faster the support team can resolve your issue. Click Submit when done.',
  },
  {
    icon: Eye,
    title: 'Track your ticket progress',
    description:
      'After submitting, you will see your ticket in the dashboard with a status (Open, In Progress, Resolved). You can add comments or reply to the support team directly from the ticket page.',
  },
];

export function SupportPage() {
  return (
    <div>
      <PageHeader
        title="Contact Support"
        subtitle="Get help from the NexaRise support team."
      />

      {/* Main CTA card */}
      <Card className="mb-6 border-gold-200 bg-gold-50/50">
        <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-serif text-lg font-semibold">Need help? Raise a support ticket</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Use the NexaRise support portal to report issues, ask questions, or request changes.
              Log in with your <span className="font-medium text-foreground">CHHABIGHAR credentials</span> — same email and password.
            </p>
          </div>
          <a
            href="https://support.nicecare.co/chat"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-gold-500 px-5 py-2.5 text-sm font-medium text-ink shadow hover:bg-gold-400 transition-colors sm:w-auto"
          >
            Open Support Portal
            <ExternalLink className="h-4 w-4" />
          </a>
        </CardContent>
      </Card>

      {/* Credentials note */}
      <Card className="mb-6">
        <CardContent className="flex items-start gap-3 p-4">
          <TicketCheck className="mt-0.5 h-5 w-5 shrink-0 text-gold-600" />
          <div className="text-sm">
            <p className="font-medium">Same credentials as CHHABIGHAR</p>
            <p className="mt-0.5 text-muted-foreground">
              You do not need a separate account. Use the same email address and password you use to log in here at CHHABIGHAR to access the support portal at{' '}
              <a
                href="https://support.nicecare.co/chat"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-gold-600 underline underline-offset-2 hover:text-gold-700"
              >
                support.nicecare.co/chat
              </a>.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Step by step guide */}
      <Card>
        <CardHeader>
          <CardTitle>How to create a ticket & track progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-0 p-0">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={i} className="flex gap-4 border-b last:border-b-0 px-6 py-5">
                {/* Step number + icon */}
                <div className="flex flex-col items-center gap-1">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-500/15 text-gold-600">
                    <Icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="w-px flex-1 bg-border" />
                  )}
                </div>
                {/* Content */}
                <div className="pb-1 pt-1.5 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Step {i + 1}
                    </span>
                  </div>
                  <h3 className="mt-0.5 font-medium text-sm">{step.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
